import * as XLSX from 'xlsx';
import { User } from '../services/types';
import { formatDate } from './reservationHelpers';

/**
 * Get role name from permission_id
 */
const getRoleName = (permissionId: number): string => {
    switch (permissionId) {
        case 1:
            return 'Normal User';
        case 2:
            return 'Admin';
        case 3:
            return 'Super Admin';
        default:
            return 'Unknown';
    }
};

/**
 * Export users data to Excel file
 * Note: Password is NOT included for security reasons
 * @param users - Array of users to export
 * @param allowedLogins - Set of user IDs that are allowed to login
 * @param filename - Optional filename (defaults to "users_export_{date}.xlsx")
 */
export const exportUsersToExcel = (
    users: User[],
    allowedLogins: Set<string>,
    filename?: string
): void => {
    // Transform users data into Excel-friendly format
    const data = users.map((user) => ({
        'User ID': user.public_id,
        'Name': user.nome,
        'Email': user.email,
        'Phone Number': user.num || '-',
        'Role': getRoleName(user.permission_id),
        'Permission ID': user.permission_id,
        'Can Login': allowedLogins.has(user.public_id) ? 'Yes' : 'No',
        'Created At': formatDate(user.created_at, 'MMM dd, yyyy HH:mm'),
        'Updated At': formatDate(user.updated_at, 'MMM dd, yyyy HH:mm'),
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths for better readability
    const colWidths = [
        { wch: 30 }, // User ID
        { wch: 25 }, // Name
        { wch: 30 }, // Email
        { wch: 20 }, // Phone Number
        { wch: 15 }, // Role
        { wch: 12 }, // Permission ID
        { wch: 10 }, // Can Login
        { wch: 20 }, // Created At
        { wch: 20 }, // Updated At
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Users');

    // Generate filename with current date if not provided
    const defaultFilename = `users_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    const finalFilename = filename || defaultFilename;

    // Write file
    XLSX.writeFile(wb, finalFilename);
};
